/**
 * IncrementalRadialLayout — position store for the cypher exploration graph.
 *
 * This module owns the `Map<string, LayoutNode>` that atom code mutates
 * incrementally. Placement itself is delegated to `RadialLayoutEngine`,
 * which is size-aware and avoids the random jitter of the previous
 * d3-force-seed approach.
 */

import { estimateLayoutNodeSize } from '../nodeSizing';

import {
	layoutDiscovery,
	placeAroundCenter,
	type NeighbourSpec,
	type NodeSize,
} from './RadialLayoutEngine';

// ============================================================================
// Types
// ============================================================================

export interface SizingInput {
	typename: string;
	title: string | null;
	isPlaceholder: boolean;
}

export interface LayoutNode {
	id: string;
	/** Graph-space X position written by `RadialLayoutEngine`. */
	x: number;
	/** Graph-space Y position written by `RadialLayoutEngine`. */
	y: number;
	/** ARI of the parent node (undefined for root). */
	parentId?: string;
	/** Typename + placeholder flag — used for sizing and icon lookup. */
	sizingInput: SizingInput;
	/** Node kind — 'node' for regular nodes, 'cluster' for collapsed groups. */
	kind: 'node' | 'cluster';
	/** When kind === 'cluster', the ARIs of the hidden members. */
	clusterMemberIds?: string[];
	/**
	 * Humanized verb describing the relationship from the parent to this
	 * node (e.g. "created", "viewed"). Surfaced as the edge label in
	 * `useCypherExplorationGraph`. Undefined for the root.
	 */
	parentRelLabel?: string;
}

export interface ChildSpec {
	id: string;
	score: number;
	typename: string;
	integration?: string;
	sizingInput: SizingInput;
	/** Humanized verb for the parent→child relationship. */
	parentRelLabel?: string;
}

export interface LayoutDelta {
	added: LayoutNode[];
	removed: string[];
	crossEdges: Array<{ from: string; to: string; relType?: string; label?: string }>;
	/**
	 * Updated rest positions for nodes that ALREADY existed before this
	 * delta. The "soft" overlap-resolution pass inside `placeAroundCenter`
	 * is allowed to nudge existing nodes a small amount to make room for
	 * the new ones. The caller (`applyDelta`) writes these positions
	 * back into `LayoutNodesAtom`; the magnetic spring then carries
	 * each affected node from its old visual position to the new rest.
	 *
	 * Empty when no existing nodes shifted (e.g. the new nodes found a
	 * clear slot on the first try).
	 */
	updated: Array<{ id: string; x: number; y: number }>;
}

// ============================================================================
// Size estimation (typename-keyed defaults until ReactFlow measures the DOM)
// ============================================================================

function estimateSize(input: SizingInput): NodeSize {
	return estimateLayoutNodeSize(input);
}

function buildSizes(
	specs: Array<{ id: string; sizingInput: SizingInput }>,
	measured?: Map<string, NodeSize>,
): Map<string, NodeSize> {
	const sizes = new Map<string, NodeSize>();
	for (const spec of specs) {
		const m = measured?.get(spec.id);
		sizes.set(spec.id, m ?? estimateSize(spec.sizingInput));
	}
	return sizes;
}

function toNeighbour(spec: ChildSpec): NeighbourSpec {
	return { id: spec.id, typename: spec.typename, title: spec.sizingInput.title };
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Initialise the layout from the root node + its immediate children.
 * Returns a fresh `Map<string, LayoutNode>`.
 *
 * `measured` is an optional id → size map populated from ReactFlow's
 * measured DOM dimensions; missing entries fall back to typename-keyed
 * estimates.
 */
export function initLayout(
	root: { id: string; sizingInput: SizingInput },
	children: ChildSpec[],
	measured?: Map<string, NodeSize>,
): Map<string, LayoutNode> {
	const sizingSpecs = [
		{ id: root.id, sizingInput: root.sizingInput },
		...children.map((c) => ({ id: c.id, sizingInput: c.sizingInput })),
	];
	const sizes = buildSizes(sizingSpecs, measured);

	const placements = layoutDiscovery({
		centerId: root.id,
		neighbours: children.map(toNeighbour),
		sizes,
	});

	const nodes = new Map<string, LayoutNode>();
	const rootPos = placements.get(root.id) ?? { x: 0, y: 0 };
	nodes.set(root.id, {
		id: root.id,
		x: rootPos.x,
		y: rootPos.y,
		sizingInput: root.sizingInput,
		kind: 'node',
	});

	for (const child of children) {
		const pos = placements.get(child.id);
		if (!pos) continue;
		nodes.set(child.id, {
			id: child.id,
			x: pos.x,
			y: pos.y,
			parentId: root.id,
			sizingInput: child.sizingInput,
			kind: 'node',
			parentRelLabel: child.parentRelLabel,
		});
	}

	return nodes;
}

/**
 * Expand a node — add new children around the parent node. Existing
 * nodes are not moved. Returns a `LayoutDelta`.
 */
export function expandLayout(
	parentId: string,
	children: ChildSpec[],
	current: Map<string, LayoutNode>,
	measured?: Map<string, NodeSize>,
): LayoutDelta {
	const parent = current.get(parentId);
	const centerPos = parent ? { x: parent.x, y: parent.y } : { x: 0, y: 0 };

	const added: LayoutNode[] = [];
	const crossEdges: Array<{ from: string; to: string; relType?: string; label?: string }> = [];

	const newChildren: ChildSpec[] = [];
	for (const child of children) {
		if (current.has(child.id)) {
			// The child node already exists on the canvas — emit a
			// cross-edge between the expanding parent and that node so
			// the relationship is rendered. Carry through the
			// `parentRelLabel` (e.g. "viewed", "owns") so the edge picks
			// up the same hover label that a brand-new child would have
			// shown via its `parentRelLabel` on the structural edge.
			crossEdges.push({ from: parentId, to: child.id, label: child.parentRelLabel });
			continue;
		}
		newChildren.push(child);
	}

	if (newChildren.length === 0) {
		return { added: [], removed: [], crossEdges, updated: [] };
	}

	// Build a sizes map covering existing nodes + the new children so the
	// placement engine can resolve overlaps against everything currently
	// on the canvas.
	const sizingSpecs: Array<{ id: string; sizingInput: SizingInput }> = [];
	for (const [, node] of current) {
		sizingSpecs.push({ id: node.id, sizingInput: node.sizingInput });
	}
	for (const c of newChildren) sizingSpecs.push({ id: c.id, sizingInput: c.sizingInput });
	const sizes = buildSizes(sizingSpecs, measured);

	const existingPositions = new Map<string, { x: number; y: number }>();
	for (const [id, node] of current) {
		existingPositions.set(id, { x: node.x, y: node.y });
	}

	// Resolve the layout root (parentless ancestor) so new neighbours can
	// be fanned on the outward side of the clicked parent.
	let rootPos: { x: number; y: number } | undefined;
	for (const [, node] of current) {
		if (!node.parentId) {
			rootPos = { x: node.x, y: node.y };
			break;
		}
	}

	const placed = placeAroundCenter({
		centerPos,
		newNeighbours: newChildren.map(toNeighbour),
		existingPositions,
		sizes,
		rootPos,
	});

	const placedById = new Map(placed.map((p) => [p.id, p]));
	for (const child of newChildren) {
		const pos = placedById.get(child.id);
		if (!pos) continue;
		added.push({
			id: child.id,
			x: pos.x,
			y: pos.y,
			parentId,
			sizingInput: child.sizingInput,
			kind: 'node',
			parentRelLabel: child.parentRelLabel,
		});
	}

	// `placeAroundCenter` mutated `existingPositions` with the post-soft-
	// resolution coordinates. Compute the diff vs. the pre-call positions
	// (from `current`) so the caller only writes back the entries that
	// actually moved. Skips nodes whose movement is below 0.5px to keep
	// the delta clean.
	const updated: Array<{ id: string; x: number; y: number }> = [];
	for (const [id, pos] of existingPositions) {
		const before = current.get(id);
		if (!before) continue;
		const dx = pos.x - before.x;
		const dy = pos.y - before.y;
		if (Math.hypot(dx, dy) < 0.5) continue;
		updated.push({ id, x: pos.x, y: pos.y });
	}

	return { added, removed: [], crossEdges, updated };
}

/**
 * Collapse a node — remove all descendants recursively.
 * Returns a `LayoutDelta` with the IDs to remove.
 */
export function collapseLayout(nodeId: string, current: Map<string, LayoutNode>): LayoutDelta {
	const toRemove: string[] = [];

	const queue: string[] = [nodeId];
	const visited = new Set<string>();
	while (queue.length > 0) {
		const id = queue.shift()!;
		if (visited.has(id)) {
			continue;
		}
		visited.add(id);
		for (const [nid, node] of current) {
			if (node.parentId === id && nid !== nodeId) {
				toRemove.push(nid);
				queue.push(nid);
			}
		}
	}

	return { added: [], removed: toRemove, crossEdges: [], updated: [] };
}
